/**
 * VERA-1095 Slice 3 — Public /signup page.
 *
 * Three-step flow:
 *  1. Email step — check eligibility via POST /signup/check-eligibility
 *     a. not allowed → invite-only message
 *     b. allowed + matchedCompanyId → Join step (employee path)
 *     c. allowed + no match → Create step (founder path)
 *  2. Join step — confirm + POST /signup/join → success message
 *  3. Create step — company name + country + plan picker → POST /signup/company → redirect to Stripe
 *
 * Slice 4 (Checkout success return + activation) is NOT here.
 */

import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, Button, CircularProgress, MenuItem, Select, Stack, Typography} from '@mui/material';
import {MENU_ITEM_SX, MENU_PAPER_SX} from '@repo/ui';
import {Input as AppInput} from 'components/UI/Input';
import {getFieldRadius} from 'components/UI/fieldMetrics';
import {AuthBackdrop} from 'components/UI/AuthBackdrop';
import {isVeraOwnedSurface} from 'utils/subdomain';
import {AuthErrorMessage} from 'components/auth';
import {createApiCall} from 'services';
import {emailRex} from 'settings';
import { VERA_SUPPORT_EMAIL } from 'constants/support';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EligibilityResult {
  allowed: boolean;
  matchedCompanyId?: string;
}

interface Plan {
  id: string;
  stripe_price_id: string;
  name: string;
  amount?: number;
  interval?: string;
}

type Step =
  | 'email'
  | 'not-allowed'
  | 'join-confirm'
  | 'join-success'
  | 'create'
  | 'redirecting';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:3040';

async function checkEligibility(email: string): Promise<EligibilityResult> {
  const res = await createApiCall({
    method: 'POST',
    url: '/signup/check-eligibility',
    data: {email},
  });
  if (res.status >= 400) {
    throw new Error(res.data?.message || 'Eligibility check failed');
  }
  return res.data as EligibilityResult;
}

async function joinCompany(email: string): Promise<{joined: boolean; companyId: string}> {
  const res = await createApiCall({
    method: 'POST',
    url: '/signup/join',
    data: {email},
  });
  if (res.status >= 400) {
    throw new Error(res.data?.message || 'Join failed');
  }
  return res.data;
}

async function createCompany(
  email: string,
  companyName: string,
  country: string,
  priceId: string,
): Promise<{checkoutUrl: string; companyId: string}> {
  const res = await createApiCall({
    method: 'POST',
    url: '/signup/company',
    data: {email, companyName, country: country || undefined, priceId},
  });
  if (res.status >= 400) {
    throw new Error(res.data?.message || 'Company creation failed');
  }
  return res.data;
}

async function fetchPlans(): Promise<Plan[]> {
  const res = await createApiCall({
    method: 'GET',
    url: '/subscriptions/get-all-plans',
  });
  if (res.status >= 400) {
    return [];
  }
  return (res.data as Plan[]) ?? [];
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const PageShell: React.FC<{children: React.ReactNode}> = ({children}) => {
  /*
    The grainy-gradient ground, shared with the sign-in page — but only on
    VERA'S OWN surface. `apps/app` is white-label, so Vera purple must never
    paint onto a brand's storefront or subdomain. `AuthBackdrop` self-gates on
    this same synchronous check; this local read governs only whether the shell
    paints its own opaque background.

    When the backdrop shows, `bgcolor` must be omitted — it is opaque and would
    hide the gradient entirely. When it does not, `background.default` stays and
    the page renders exactly as it did before the backdrop existed.
  */
  const veraOwnedSurface = isVeraOwnedSurface();
  return (
    <>
      {veraOwnedSurface && <AuthBackdrop />}
      <Box
        sx={{
          /* The backdrop is a fixed layer at z-index 0; without an explicit
             stacking position this content would render beneath it. */
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          pt: '20vh',
          pb: 6,
          px: 2,
          ...(veraOwnedSurface ? {} : {bgcolor: 'background.default'}),
        }}
      >
        <Box sx={{width: '100%', maxWidth: 400}}>
          <Typography component="h2" sx={{fontSize: '24px', lineHeight: '32px', fontWeight: 400, mb: 4}}>
            Create account
          </Typography>
          {children}
        </Box>
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  // Shared state
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [matchedCompanyId, setMatchedCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Create step state
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPriceId, setSelectedPriceId] = useState('');
  const [plansLoading, setPlansLoading] = useState(false);

  // ------------------------------------------------------------------
  // Step 1: Email → eligibility check
  // ------------------------------------------------------------------
  const handleEmailSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    if (!emailRex.test(trimmedEmail)) {
      setError('Enter a valid email');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await checkEligibility(trimmedEmail);
      if (!result.allowed) {
        setStep('not-allowed');
        return;
      }
      if (result.matchedCompanyId) {
        setMatchedCompanyId(result.matchedCompanyId);
        setStep('join-confirm');
      } else {
        // Load plans for the create step
        setPlansLoading(true);
        const fetchedPlans = await fetchPlans();
        setPlans(fetchedPlans);
        if (fetchedPlans.length > 0) {
          setSelectedPriceId(fetchedPlans[0].stripe_price_id);
        }
        setPlansLoading(false);
        setStep('create');
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Step 2a: Join confirm
  // ------------------------------------------------------------------
  const handleJoinConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      await joinCompany(email.trim());
      setStep('join-success');
    } catch (err: any) {
      setError(err?.message || 'Join failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Step 2b: Create company → Stripe Checkout
  // ------------------------------------------------------------------
  const handleCreate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!companyName.trim()) {
      setError('Please enter a company name.');
      return;
    }
    if (!selectedPriceId) {
      setError('Please select a plan.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const {checkoutUrl} = await createCompany(
        email.trim(),
        companyName.trim(),
        country.trim(),
        selectedPriceId,
      );
      setStep('redirecting');
      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err?.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  // Email step
  if (step === 'email') {
    return (
      <PageShell>
        <form noValidate onSubmit={handleEmailSubmit}>
          <Stack spacing={2}>
            <AppInput
              label="Email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={setEmail}
              disabled={loading}
              error={!!error}
              sx={{'& input': {paddingLeft: '8px'}}}
            />
            {error && <AuthErrorMessage message={error} />}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              onClick={() => handleEmailSubmit()}
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{paddingX: 2}}
            >
              Continue
            </Button>
            <Box sx={{textAlign: 'center'}}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Typography
                  component="span"
                  variant="body2"
                  onClick={() => navigate('/sign-in')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Log in
                </Typography>
              </Typography>
            </Box>
          </Stack>
        </form>
      </PageShell>
    );
  }

  // Not allowed — invite-only
  if (step === 'not-allowed') {
    return (
      <PageShell>
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight={500}>
            Signup is invite-only
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your email address is not on the invite list. Vera is currently in
            private beta. Reach out to{' '}
            <a href={`mailto:${VERA_SUPPORT_EMAIL}`} style={{color: 'inherit'}}>
              {VERA_SUPPORT_EMAIL}
            </a>{' '}
            to request access.
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {setStep('email'); setError(null);}}
            sx={{paddingX: 2}}
          >
            Try a different email
          </Button>
        </Stack>
      </PageShell>
    );
  }

  // Join confirm step (employee path)
  if (step === 'join-confirm') {
    return (
      <PageShell>
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight={500}>
            Your company already has a Vera account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We'll add <strong>{email}</strong> as a team member and send you an
            invitation to set up your login.
          </Typography>
          {error && <AuthErrorMessage message={error} />}
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => {setStep('email'); setError(null);}}
              disabled={loading}
              sx={{paddingX: 2}}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleJoinConfirm}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{paddingX: 2}}
            >
              Join company
            </Button>
          </Stack>
        </Stack>
      </PageShell>
    );
  }

  // Join success
  if (step === 'join-success') {
    return (
      <PageShell>
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight={500}>
            Check your email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We've sent an invitation to <strong>{email}</strong>. Follow the
            link to finish setting up your account.
          </Typography>
        </Stack>
      </PageShell>
    );
  }

  // Create step (founder path)
  if (step === 'create') {
    return (
      <PageShell>
        <form noValidate onSubmit={handleCreate}>
          <Stack spacing={2}>
            <AppInput
              label="Company name"
              placeholder="Acme Inc."
              value={companyName}
              onChange={setCompanyName}
              disabled={loading}
              sx={{'& input': {paddingLeft: '8px'}}}
            />
            <AppInput
              label="Country (optional)"
              placeholder="US"
              value={country}
              onChange={setCountry}
              disabled={loading}
              sx={{'& input': {paddingLeft: '8px'}}}
            />

            {/* Plan selector */}
            {plansLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading plans…
                </Typography>
              </Box>
            ) : plans.length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{mb: 1}}>
                  Plan
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={selectedPriceId}
                  onChange={(e) => setSelectedPriceId(e.target.value)}
                  disabled={loading}
                  // A Select's option list is a floating menu paper, and in THIS app
                  // nothing supplies it with chrome: `apps/app`'s MuiMenu theme override
                  // sets only `container` (the portal), unlike the platform theme which
                  // styles MuiMenu.paper/list and MuiMenuItem globally. So left bare this
                  // dropdown renders MUI defaults — 4px paper radius, an 8px/0 list inset
                  // and square 6px/16px rows. Same MenuProps shape as the app's own
                  // `components/Fields/SelectField`.
                  MenuProps={{
                    slotProps: {
                      paper: {
                        sx: {
                          ...MENU_PAPER_SX,
                          borderRadius: '12px',
                          boxShadow: '0px 4px 12px -4px rgba(0,0,0,0.25)',
                          '& .MuiMenuItem-root': MENU_ITEM_SX,
                        },
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#f6f6f6',
                    // VRG-562: a FIELD, so the clamped field radius — the same value the
                    // `AppInput`s stacked above it resolve to. It was a literal 12px,
                    // which both ignored the tenant AND sat 6px rounder than its own
                    // neighbours on this form.
                    borderRadius: theme => `${getFieldRadius(theme.shape.borderRadius)}px`,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.300',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => (theme.palette as any)?.brand?.[600] || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => (theme.palette as any)?.brand?.[600] || theme.palette.primary.main,
                    },
                  }}
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.stripe_price_id} value={plan.stripe_price_id}>
                      {plan.name}
                      {plan.amount != null && plan.interval
                        ? ` — $${(plan.amount / 100).toFixed(0)}/${plan.interval}`
                        : ''}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No plans available. Please contact{' '}
                <a href={`mailto:${VERA_SUPPORT_EMAIL}`} style={{color: 'inherit'}}>
                  {VERA_SUPPORT_EMAIL}
                </a>.
              </Typography>
            )}

            {error && <AuthErrorMessage message={error} />}

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                onClick={() => {setStep('email'); setError(null);}}
                disabled={loading}
                sx={{paddingX: 2}}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                onClick={() => handleCreate()}
                disabled={loading || !companyName.trim() || !selectedPriceId || plansLoading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{paddingX: 2}}
              >
                Continue to payment
              </Button>
            </Stack>
          </Stack>
        </form>
      </PageShell>
    );
  }

  // Redirecting to Stripe
  return (
    <PageShell>
      <Box sx={{textAlign: 'center', py: 4}}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Redirecting to checkout…
        </Typography>
      </Box>
    </PageShell>
  );
};

export default SignupPage;
